import { useEffect, useRef, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';

const client = generateClient({ authMode: 'userPool' });

/**
 * #19 reconciler UI (#153 slice 2 section C, 153-slice2-contract.md,
 * binding): lists every COMPLETED PaymentRecord whose side effect wasn't
 * applied (process-payment returned RECONCILIATION_REQUIRED, or the write
 * was otherwise missed), and lets an admin apply the missing update.
 *
 * Calls the `reconcilePayment(action, paymentRecordId)` mutation the lead
 * wires (allow.groups(['AdminsVerified'])). Response is JSON.stringify'd by
 * the Lambda, so it's parsed with parseLambdaResponse, same as
 * decideFamilyChangeRequestClient.ts / manageFamilyClient.ts.
 *
 * No required props — the lead mounts this directly into the admin
 * dashboard (Reports or Payments tab).
 */

interface AttentionItem {
  paymentRecordId: string;
  entityId: string;
  entityType: string;
  paymentType: string;
  amountCents: number;
  timestamp: string;
  memberNumber?: string | null;
  participantName?: string | null;
  reason: string;
  reasonText: string;
  entityExists: boolean;
}

function formatPaymentType(paymentType: string): string {
  switch (paymentType) {
    case 'MEMBERSHIP_NEW': return 'New membership';
    case 'MEMBERSHIP_RENEWAL': return 'Renewal';
    case 'COMPETITION_ENTRY': return 'Competition entry';
    case 'FAMILY_MEMBER_ADD': return 'Family member add';
    default: return paymentType;
  }
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
}

const PaymentsNeedingAttention: React.FC = () => {
  const [items, setItems] = useState<AttentionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [confirmItem, setConfirmItem] = useState<AttentionItem | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const rowButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (confirmItem) cancelRef.current?.focus();
  }, [confirmItem]);

  const load = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const result = await (client.mutations as any).reconcilePayment({ action: 'LIST' });
      if (result?.errors?.length) {
        setLoadError(result.errors[0]?.message || 'Failed to load payments needing attention.');
        return;
      }
      const data = parseLambdaResponse<{ success?: boolean; items?: AttentionItem[]; error?: string; message?: string }>(result?.data);
      if (!data?.success) {
        setLoadError(data?.message || 'Failed to load payments needing attention.');
        return;
      }
      setItems(data.items || []);
    } catch (e: any) {
      setLoadError(e?.message || 'Failed to load payments needing attention.');
    } finally {
      setLoading(false);
    }
  };

  const closeConfirm = (paymentRecordId?: string) => {
    setConfirmItem(null);
    setApplyError(null);
    if (paymentRecordId) {
      setTimeout(() => rowButtonRefs.current[paymentRecordId]?.focus(), 0);
    }
  };

  const applyPayment = async () => {
    if (!confirmItem) return;
    setApplying(true);
    setApplyError(null);
    try {
      const result = await (client.mutations as any).reconcilePayment({
        action: 'APPLY',
        paymentRecordId: confirmItem.paymentRecordId,
      });
      if (result?.errors?.length) {
        throw new Error(result.errors[0]?.message || 'Failed to apply payment.');
      }
      const data = parseLambdaResponse<{ success?: boolean; error?: string; message?: string }>(result?.data);
      if (!data?.success) {
        throw new Error(data?.message || data?.error || 'Failed to apply payment.');
      }
      setStatusMessage(data.message || 'Applied.');
      setConfirmItem(null);
      await load();
    } catch (e: any) {
      setApplyError(e?.message || 'Failed to apply payment.');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return <div className="py-8 text-center text-gray-500">Loading payments needing attention…</div>;
  }

  if (loadError) {
    return (
      <div className="py-4">
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{loadError}</div>
        <button
          type="button"
          onClick={load}
          className="min-h-[44px] rounded-md bg-fishing-blue px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white p-4 shadow sm:p-6">
      <h2 className="text-lg font-bold text-gray-900">Payments needing attention ({items.length})</h2>
      <div aria-live="polite" className="sr-only">
        {items.length === 0 ? 'No payments need attention.' : `${items.length} payments need attention.`}
      </div>

      {statusMessage && (
        <p role="status" aria-live="polite" className="mt-3 rounded-md border border-green-200 bg-green-50 p-2 text-sm font-medium text-green-800">
          {statusMessage}
        </p>
      )}

      {items.length === 0 ? (
        <p className="mt-4 text-sm text-gray-500">No payments need attention.</p>
      ) : (
        <ul className="mt-4 space-y-2">
          {items.map((item) => {
            const who = item.participantName || item.memberNumber || item.entityId;
            return (
              <li key={`${item.paymentRecordId}-${item.entityId}`} className="rounded-md border border-gray-200 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="font-medium text-gray-900">
                      {who} · {formatPaymentType(item.paymentType)} · ${((item.amountCents ?? 0) / 100).toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-600">{formatWhen(item.timestamp)}</div>
                    <div className="text-sm text-amber-700">{item.reasonText}</div>
                  </div>
                  <button
                    type="button"
                    ref={(el) => { rowButtonRefs.current[item.paymentRecordId] = el; }}
                    onClick={() => setConfirmItem(item)}
                    disabled={!item.entityExists}
                    className="min-h-[44px] rounded-lg bg-fishing-blue px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Apply payment
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {confirmItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="apply-payment-title"
            aria-describedby="apply-payment-desc"
            onKeyDown={(e) => {
              if (e.key === 'Escape' && !applying) closeConfirm(confirmItem.paymentRecordId);
            }}
            className="w-full max-w-md rounded-xl bg-white p-5 shadow-2xl"
          >
            <h2 id="apply-payment-title" className="text-lg font-bold text-gray-900">Apply payment?</h2>
            <div id="apply-payment-desc" className="mt-2 space-y-2 text-sm text-gray-700">
              <p>
                This marks <strong>{confirmItem.participantName || confirmItem.memberNumber || confirmItem.entityId}</strong>&apos;s{' '}
                {formatPaymentType(confirmItem.paymentType).toLowerCase()} (${((confirmItem.amountCents ?? 0) / 100).toFixed(2)}) as paid.
                The card was already charged; this only updates the record that was missed.
              </p>
            </div>
            {applyError && (
              <p role="alert" aria-live="assertive" className="mt-3 text-sm font-medium text-red-700">{applyError}</p>
            )}
            <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                ref={cancelRef}
                type="button"
                onClick={() => closeConfirm(confirmItem.paymentRecordId)}
                disabled={applying}
                className="min-h-[44px] rounded-lg border border-gray-300 px-4 py-2 font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyPayment}
                disabled={applying}
                className="min-h-[44px] rounded-lg bg-fishing-blue px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {applying ? 'Applying…' : 'Apply payment'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentsNeedingAttention;
